import { Grid } from '../core/Grid.js';
import { GameType, GameOrder } from '../persistence/types.js';
import { getTutorial, getVortexTutorial, getMetaTutorial, TutorialEntry } from '../tutorials/TutorialRegistry.js';

export interface WelcomePanelCallbacks {
    /** The player picked a game order at the bottom of the panel; persist + apply + close. */
    onChooseOrder: (order: GameOrder) => void;
    /** The player dismissed the Vortex welcome ("Got it"); just close it. */
    onVortexDismiss: () => void;
}

/**
 * Context for the Vortex welcome, derived from the grid being opened. Drives the
 * conditional congratulations (this size was earned by clearing 3 challenges) and
 * the reminder about larger sizes still locked behind their own challenge clears.
 */
export interface VortexWelcomeContext {
    /** Preset tier of the opened grid: 'Tutorial' (6×6), 'Medium' (8×8), 'Standard' (10×10). */
    sizeLabel: string;
    /** True when this size was unlocked by clearing 3 challenges (i.e. not the always-open Tutorial). */
    earnedUnlock: boolean;
    /** Larger Vortex sizes still locked, each with the challenge tier that unlocks it. */
    lockedSizes: Array<{ label: string; dims: string }>;
}

/** A sidebar control explained with its own icon. */
interface IconItem {
    /** Inline SVG markup, lifted from the matching sidebar button in index.html. */
    svg: string;
    name: string;
    body: string;
}

/**
 * One illustrated section: either prose paired with grid previews, or (when `icons`
 * is set) a guide that explains a couple of sidebar controls by their icons.
 */
interface WelcomeSection {
    heading: string;
    body?: string;
    /** Tutorial previews to render beside the text. */
    previews?: Array<{ game: GameType } | { vortexGame: GameType } | { meta: 'basics' | 'vortex-hub'; scene?: number }>;
    /** Icon-guide rows (mutually exclusive with previews). */
    icons?: IconItem[];
}

// Sidebar control glyphs, kept in sync with index.html (#grids-btn, #reset,
// #help-btn, #auto-start).
const GRIDS_ICON =
    '<svg viewBox="0 0 22 22" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><rect x="2" y="2" width="18" height="18" rx="1.5"/><line x1="6.5" y1="2" x2="6.5" y2="20"/><line x1="11" y1="2" x2="11" y2="20"/><line x1="15.5" y1="2" x2="15.5" y2="20"/><line x1="2" y1="6.5" x2="20" y2="6.5"/><line x1="2" y1="11" x2="20" y2="11"/><line x1="2" y1="15.5" x2="20" y2="15.5"/></svg>';
const RESET_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>';
const HELP_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>';
const AUTOSTART_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="19.5" width="7" height="3" rx="0.6"/><line x1="6.5" y1="19.5" x2="9" y2="11.5"/><circle cx="9" cy="11.5" r="1.3"/><line x1="10" y1="11" x2="16" y2="7"/><path d="M18.5 3.8 A2.4 2.4 0 0 0 18.5 8.6"/></svg>';
// A keyboard outline with a few keys — the "Keyboard mode" welcome guide glyph.
const KEYBOARD_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="1.5" y="6" width="21" height="12" rx="2"/><line x1="5" y1="9.5" x2="5" y2="9.5"/><line x1="8.5" y1="9.5" x2="8.5" y2="9.5"/><line x1="12" y1="9.5" x2="12" y2="9.5"/><line x1="15.5" y1="9.5" x2="15.5" y2="9.5"/><line x1="19" y1="9.5" x2="19" y2="9.5"/><line x1="7.5" y1="14.5" x2="16.5" y2="14.5"/></svg>';

// Vortex sidebar control glyphs, kept in sync with index.html (#vortex-hub-btn,
// #vortex-activate-btn).
const VORTEX_HUB_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2.5" y="3" width="7" height="7" rx="2"/><rect x="14.5" y="14" width="7" height="7" rx="2"/><path d="M9.5 6.5 C 16 6.5, 8 17.5, 14.5 17.5"/></svg>';
const VORTEX_ACTIVATE_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3.5" width="8" height="8" rx="2" fill="currentColor" stroke="none"/><rect x="13" y="12.5" width="8" height="8" rx="2"/></svg>';

// Paired previews illustrate "same grid, different game", so each pair must share
// an identical tutorial grid configuration (digit positions, not just size/range):
//   branches & walls → 6/1-3:1E2,2A3,3D4   ·   flood & antennas → 6/1-3:1D1,2A2,3E3
const SECTIONS: WelcomeSection[] = [
    {
        heading: 'One grid, twelve puzzles',
        body: 'Every game is played on the same grid of digits! But each of the twelve games has its own rules and its own way to win.',
        previews: [{ game: 'branches' }, { game: 'walls' }],
    },
    {
        heading: 'Reach the digits',
        body: 'Most puzzles ask you to reach the digits in order, or to form a cycle between them. The digits stay put; you place the elements that connect them.',
        previews: [{ meta: 'basics', scene: 1 }],
    },
    {
        heading: 'Switch anytime',
        body: 'Use the game buttons on the right to jump between the twelve games whenever you like. Your progress is saved per grid, so nothing is lost.',
        previews: [{ game: 'flood' }, { game: 'antennas' }],
    },
    {
        heading: 'Grids and resets',
        icons: [
            { svg: GRIDS_ICON, name: 'Grids', body: 'Browse and switch between your saved grids. The top row opens the grid creator to pick a preset and generate a fresh one.' },
            { svg: RESET_ICON, name: 'Reset', body: 'Clears your placements on the current game only — the grid itself and your other games are left untouched.' },
        ],
    },
    {
        heading: 'Two buttons worth knowing',
        icons: [
            { svg: HELP_ICON, name: 'How to play', body: 'Opens a step-by-step tutorial for every game — and this welcome — so you can learn each mechanic at your own pace.' },
            { svg: AUTOSTART_ICON, name: 'Auto-start', body: 'On an empty board, places the first element or two needed to reach digit 1 — a nudge for when you are not sure where to begin.' },
        ],
    },
    {
        heading: 'Lower scores are better',
        body: 'Solving a puzzle earns a score. The fewer elements you place, the better the score. Once you have a solution, try to trim it down!',
        previews: [{ game: 'mirrors' }],
    },
    {
        heading: 'Prefer the keyboard?',
        icons: [
            { svg: KEYBOARD_ICON, name: 'Keyboard mode', body: 'Play any game without the mouse: move a crosshair with the arrows or WASD, place with Space, delete with C — and hold Space to trace a whole path at once. Turn it on in Settings, or press K in a game; Settings lists every shortcut.' },
        ],
    },
];

/**
 * The Vortex welcome's sections, assembled per grid: a fixed core (the extra digit
 * and shared gate network, the two new buttons, the Vortex tutorials, set-submission)
 * bracketed by the context-driven congratulations and unlock reminder.
 */
function vortexSections(ctx: VortexWelcomeContext): WelcomeSection[] {
    const sections: WelcomeSection[] = [];

    // Congratulate a player who earned this size by clearing three challenges.
    if (ctx.earnedUnlock) {
        sections.push({
            heading: 'Nicely done',
            body: `You unlocked Vortex ${ctx.sizeLabel} by clearing three ${ctx.sizeLabel} challenges. Here is the extra challenge you earned.`,
        });
    }

    sections.push(
        {
            heading: 'One more digit, one shared network',
            body: 'A Vortex grid adds a zero to the digits and ties all twelve games together through a shared network of gate pairs. Build the network once and every game reads from it — gates act as teleporters, each bending its game’s rules.',
            previews: [{ meta: 'vortex-hub' }],
        },
        {
            heading: 'Two buttons to know',
            icons: [
                { svg: VORTEX_HUB_ICON, name: 'Vortex Hub', body: 'Opens the shared Links view, where you create, link, and delete gate pairs. This one network is used by all twelve games.' },
                { svg: VORTEX_ACTIVATE_ICON, name: 'Activate Vortex', body: 'Inside a game, toggles which gate pairs are active for that puzzle — and lets you spin up new pairs without leaving the board.' },
            ],
        },
        {
            heading: 'Tutorials, the Vortex way',
            body: 'Every game’s tutorial has a Vortex edition that shows how gates change its rules. Find them in How to Play whenever you want a refresher.',
            previews: [{ vortexGame: 'mirrors' }],
        },
        {
            heading: 'Solutions travel as a set',
            body: 'On a Vortex challenge, all your game solutions are submitted together as one block. The gate network couples them, so the whole set stands or falls on its combined score — improve them together, not piecemeal.',
        },
    );

    // Remind the player which larger sizes are still locked, and how to open them.
    if (ctx.lockedSizes.length > 0) {
        const list = ctx.lockedSizes
            .map(s => `clear three ${s.label} challenges to unlock Vortex ${s.label} (${s.dims})`)
            .join(', and ');
        const lead = ctx.lockedSizes.length > 1 ? 'There are larger Vortex grids waiting' : 'There is a larger Vortex grid waiting';
        sections.push({
            heading: 'More to unlock',
            body: `${lead}: ${list}.`,
        });
    }

    return sections;
}

/**
 * "Welcome to Grid Games" intro panel: a scrollable, illustrated explainer shown on
 * a fresh install (and re-openable from the Tutorial Hub). It ends by asking whether
 * the game buttons should appear in difficulty order or alphabetically.
 */
export class WelcomePanel {
    private containerEl: HTMLElement;
    private callbacks: WelcomePanelCallbacks;

    constructor(containerEl: HTMLElement, callbacks: WelcomePanelCallbacks) {
        this.containerEl = containerEl;
        this.callbacks = callbacks;
    }

    show(): void {
        this.render();
        this.containerEl.classList.remove('hidden', 'vortex');
        this.containerEl.scrollTop = 0;
    }

    /** Show the Vortex variant: same layout, Vortex-specific content + a plain dismiss. */
    showVortex(ctx: VortexWelcomeContext): void {
        this.renderVortex(ctx);
        this.containerEl.classList.remove('hidden');
        this.containerEl.classList.add('vortex');  // purple theme for the title
        this.containerEl.scrollTop = 0;
    }

    hide(): void {
        this.containerEl.classList.add('hidden');
    }

    private render(): void {
        const el = this.renderShell(
            'Welcome to Grid Games',
            'A collection of twelve puzzles that all share one grid. Here is the gist before you dive in.',
            SECTIONS,
        );
        el.appendChild(this.renderOrderChooser());
    }

    private renderVortex(ctx: VortexWelcomeContext): void {
        const el = this.renderShell(
            'Welcome to Vortex Mode',
            'The same twelve puzzles, now woven together by a shared network of gates. Here is what is new.',
            vortexSections(ctx),
        );
        el.appendChild(this.renderDismiss());
    }

    /** Render the title, intro, and sections shell shared by both welcome variants. */
    private renderShell(titleText: string, introText: string, sections: WelcomeSection[]): HTMLElement {
        const el = this.containerEl;
        el.innerHTML = '';

        const title = document.createElement('h1');
        title.className = 'welcome-title';
        title.textContent = titleText;
        el.appendChild(title);

        const intro = document.createElement('p');
        intro.className = 'welcome-intro';
        intro.textContent = introText;
        el.appendChild(intro);

        // Alternation parity counts only preview sections, so a full-width icon
        // guide in the middle doesn't flip the rhythm of the ones after it.
        let previewIndex = 0;
        for (const section of sections) {
            const index = section.icons ? -1 : previewIndex++;
            el.appendChild(this.renderSection(section, index));
        }
        return el;
    }

    private renderSection(section: WelcomeSection, index: number): HTMLElement {
        if (section.icons) return this.renderIconGuide(section, section.icons);

        const row = document.createElement('div');
        row.className = 'welcome-section';

        const text = document.createElement('div');
        text.className = 'welcome-text';
        const h = document.createElement('h2');
        h.className = 'welcome-heading';
        h.textContent = section.heading;
        const body = document.createElement('p');
        body.textContent = section.body ?? '';
        text.append(h, body);

        // Text-only sections (the Vortex congratulations / unlock reminder) skip the
        // preview column entirely, so there's no stray flex gap on the empty side.
        if (!section.previews?.length) {
            row.classList.add('welcome-section-text');
            row.append(text);
            return row;
        }

        // Alternate which side the previews sit on, section by section.
        if (index % 2 === 1) row.classList.add('reverse');

        const previews = document.createElement('div');
        previews.className = 'welcome-previews';
        for (const p of section.previews) {
            const entry = 'meta' in p ? getMetaTutorial(p.meta)
                : 'vortexGame' in p ? getVortexTutorial(p.vortexGame)
                : getTutorial(p.game);
            if (!entry) continue;
            const canvas = document.createElement('canvas');
            canvas.className = 'welcome-preview';
            canvas.width = 220;
            canvas.height = 220;
            this.renderPreview(canvas, entry, 'meta' in p ? p.scene : undefined);
            previews.appendChild(canvas);
        }

        row.append(previews, text);
        return row;
    }

    /** Full-width section explaining a couple of sidebar controls by their icons. */
    private renderIconGuide(section: WelcomeSection, icons: IconItem[]): HTMLElement {
        const row = document.createElement('div');
        row.className = 'welcome-section welcome-section-guide';

        const h = document.createElement('h2');
        h.className = 'welcome-heading';
        h.textContent = section.heading;
        row.appendChild(h);

        const list = document.createElement('div');
        list.className = 'welcome-guide-list';
        for (const item of icons) {
            const itemRow = document.createElement('div');
            itemRow.className = 'welcome-guide-item';

            const glyph = document.createElement('span');
            glyph.className = 'welcome-guide-icon';
            glyph.innerHTML = item.svg;

            const text = document.createElement('div');
            text.className = 'welcome-guide-text';
            const name = document.createElement('div');
            name.className = 'welcome-guide-name';
            name.textContent = item.name;
            const body = document.createElement('p');
            body.textContent = item.body;
            text.append(name, body);

            itemRow.append(glyph, text);
            list.appendChild(itemRow);
        }
        row.appendChild(list);
        return row;
    }

    /** Render a tutorial scene's grid onto a preview canvas (mirrors TutorialHub). */
    private renderPreview(canvas: HTMLCanvasElement, entry: TutorialEntry, sceneIndex?: number): void {
        const { definition, createAdapter } = entry;
        const grid = Grid.deserialize(definition.gridCode);
        const adapter = createAdapter(canvas, grid);
        const idx = sceneIndex ?? definition.previewScene ?? 0;
        const scene = definition.scenes[idx] ?? definition.scenes[0];
        adapter.applyState(scene.gameState);
        adapter.render(scene.renderOptions);
    }

    private renderOrderChooser(): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'welcome-chooser';

        const prompt = document.createElement('p');
        prompt.className = 'welcome-chooser-prompt';
        prompt.textContent = 'Some of the twelve games are trickier than others. Do you want the games to appear in rough difficulty order, or alphabetical?';
        wrap.appendChild(prompt);

        const buttons = document.createElement('div');
        buttons.className = 'welcome-chooser-buttons';
        buttons.append(
            this.orderButton('Alphabetical', 'alphabetical'),
            this.orderButton('Difficulty order', 'difficulty'),
        );
        wrap.appendChild(buttons);
        return wrap;
    }

    private orderButton(label: string, order: GameOrder): HTMLButtonElement {
        const b = document.createElement('button');
        b.className = 'welcome-btn';
        b.textContent = label;
        b.addEventListener('click', () => this.callbacks.onChooseOrder(order));
        return b;
    }

    /** Closing control for the Vortex welcome — it has no order to choose. */
    private renderDismiss(): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'welcome-chooser';

        const buttons = document.createElement('div');
        buttons.className = 'welcome-chooser-buttons';
        const b = document.createElement('button');
        b.className = 'welcome-btn';
        b.textContent = 'Got it';
        b.addEventListener('click', () => this.callbacks.onVortexDismiss());
        buttons.appendChild(b);

        wrap.appendChild(buttons);
        return wrap;
    }
}
